import {
  UserInfo, ApiResponse, MessagesToUserInfo, SurveyManagerAccessInfoData,
  SupportEmailAddressData, AdminInfoData, ToolsInfoData, VeracityPolicyData
} from 'app/types-shared';
import { RemoteData, RemoteAdminData, RemoteNotificationsData, RemoteVeracityPolicyData } from '../infrastructure/remote-data';
import { AppSetting } from 'dependencies/appsettings';
import { CodetableWireFormat } from 'app/infrastructure/ui/form/types-codetable';
import { Consent } from 'app/area/user-profile/my-notifications/types';

const endpoint = {
  userInfo: 'user/current',
  supportEmailAddress: 'help/support/emailAddress',
  version: 'version',
  refreshCache: 'cache/refresh',
  regenerateAggregatedEnv: 'case/aggregatedata/environmental',
  regenerateAggregatedRisk: 'case/aggregatedata/risk',
  helpUrl: 'help/url',
  messagesToUser: 'user/current/message',
  loadConsents: (recipientId: number) => `Recipient/${recipientId}/channel`,
  surveyManagerAccess: 'user/current/access/surveyManager',
  getExternalUrl: (externalUrlId: number) => `externalUrl/${externalUrlId}`,
  feedbackUrl: 'feedback/url'
};

const adminEndpoint = {
  adminPanelAccess: 'user/adminPanelAccess'
};

const toolsEndpoint = {
  toolsAccess: 'user/current/access/tools'
};

export const api = {

  /**
   * Fetch userinfo based on authentication context
   */
  userInfo: async (): Promise<UserInfo> => {
    const remote = new RemoteData(endpoint.userInfo);
    const response = await remote.resource<ApiResponse<UserInfo>>().single();
    return response.data;
  },

  userAdminAccess: async (): Promise<AdminInfoData | boolean> => {
    if (!AppSetting.AdminApiBaseUrl) {
      return false;
    }
    let response = undefined;
    const remote = new RemoteAdminData(adminEndpoint.adminPanelAccess);
    try {
      response = await remote.resource<ApiResponse<AdminInfoData>>().withImportance('low').single();
    } catch {
      // admin api misconfiguration should not break app login
      return false;
    }
    return response.data;
  },

  userToolsInfo: async (): Promise<ToolsInfoData> => {
    const remote = new RemoteData(toolsEndpoint.toolsAccess);
    const response = await remote.resource<ApiResponse<ToolsInfoData>>().withImportance('low').single();
    return response.data;
  },

  supportEmailAddress: async (): Promise<SupportEmailAddressData> => {
    const remote = new RemoteData(endpoint.supportEmailAddress);
    const response = await remote.resource<ApiResponse<SupportEmailAddressData>>().withImportance('low').single();
    return response.data;
  },

  version: async (): Promise<any> => {
    const response = await new RemoteData(endpoint.version)
      .resource<any>()
      .withImportance('low')
      .single();
    return response.version;
  },

  refreshCache: async (): Promise<any> => {
    const response = await new RemoteData(endpoint.refreshCache).resource<any>().create('');

    return await response;
  },

  regenerateAggregatedEnv: async (): Promise<any> => {
    const response = await new RemoteData(endpoint.regenerateAggregatedEnv).resource<any>().create('');

    return await response;
  },

  regenerateAggregatedRisk: async (): Promise<any> => {
    const response = await new RemoteData(endpoint.regenerateAggregatedRisk).resource<any>().create('');

    return await response;
  },

  regenerateAggregatedTables: async (regenerateEndpoint: string): Promise<any> => {

    const response = await new RemoteAdminData(regenerateEndpoint)
      .resource<any>()
      .create('');

    return await response;
  },

  helpUrl: async (): Promise<string> => {
    const response = await new RemoteData(endpoint.helpUrl)
      .resource<ApiResponse<{ urlTemplate: string }>>()
      .withImportance('low')
      .single();

    return response.data.urlTemplate;
  },

  messagesToUser: async (): Promise<MessagesToUserInfo[]> => {
    const response = await new RemoteData(endpoint.messagesToUser)
      .resource<ApiResponse<MessagesToUserInfo>>()
      .withImportance('low')
      .single();

    return await (response.data) as any;
  },

  updateMessageStatus: async (id: number, status: CodetableWireFormat): Promise<any> => {
    const response = await new RemoteData(endpoint.messagesToUser).resource<any>()
      .withId(id)
      .save({ status });
    return await response;
  },

  loadConsents: async (recipientId: number): Promise<Consent[]> => {
    return await new RemoteNotificationsData(endpoint.loadConsents(recipientId))
      .resource<Consent[]>()
      .withImportance('low')
      .all();
  },

  checkVeracityPolicy: async (redirectUri?: string): Promise<VeracityPolicyData> => {
    return await new RemoteVeracityPolicyData(redirectUri)
      .resource<VeracityPolicyData>()
      .withImportance('low')
      .single();
  },

  surveyManagerAccess: async (): Promise<SurveyManagerAccessInfoData> => {
    const response = await new RemoteData(endpoint.surveyManagerAccess)
      .resource<ApiResponse<SurveyManagerAccessInfoData>>()
      .withImportance('low')
      .single();

    return await (response.data) as any;
  },

  manageSurveysUrl: async (): Promise<string> => {
    const response = await new RemoteData(endpoint.getExternalUrl(4))
      .resource<ApiResponse<{ urlTemplate: string }>>()
      .withImportance('low')
      .single();

    return response.data.urlTemplate;
  },

  feedbackUrl: async (): Promise<string> => {
    const response = await new RemoteData(endpoint.feedbackUrl)
      .resource<ApiResponse<{ urlTemplate: string }>>()
      .withImportance('low')
      .single();

    return response.data.urlTemplate;
  }
};
